import {
    Input,
    ColorPicker,
    ColorPickerRef,
    ColorSizePicker,
    ColorSizePickerRef,
    SizePicker,
    SizePickerRef,
  } from "@components";
  import { VariationTypeEnum } from "@utilities/enums";
import { Variations } from "@utilities/types";
  import {
    useCallback,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
  } from "react";
  
  export interface ProductVariationRef {
    resetVariation: () => void;
  }
  
  interface Props {
    initialVariation?: Variations | null;
    forceVariationType?: VariationTypeEnum;
    onChange: (variationType: VariationTypeEnum, data: any) => void;
  }
  
  export const ProductVariation = forwardRef<ProductVariationRef, Props>(
    ({ initialVariation, forceVariationType, onChange }, ref) => {

        const colorSizePickerRef = useRef<ColorSizePickerRef>(null);
        const sizePickerRef = useRef<SizePickerRef>(null);
        const colorPickerRef = useRef<ColorPickerRef>(null);
    
        const [variationType, setVariationType] = useState<VariationTypeEnum>(forceVariationType! || VariationTypeEnum.NONE);
        const [quantity, setQuantity] = useState(0);

        useEffect(()=> {
            if(!initialVariation){
                let variationData:Variations = { quantity: 0 };
                if (variationType === VariationTypeEnum.COLOR) {
                    variationData = { colors: [] };
                } else if (variationType === VariationTypeEnum.SIZE) {
                    variationData = { sizes: [] };
                } else if (variationType === VariationTypeEnum.BOTH) {
                    variationData = { colorSizeData: [] };
                }

                onChange(variationType, variationData);

            };
        }, [initialVariation, variationType]);
    
        const forceIs = useCallback(
            (type: VariationTypeEnum) =>
            !!forceVariationType && forceVariationType !== type,
            [forceVariationType]
        );
    
        const resetVariation = useCallback(() => {
            setVariationType(VariationTypeEnum.NONE);
            setQuantity(0);
            onChange(VariationTypeEnum.NONE, { quantity: 0 });
    
            colorSizePickerRef.current?.resetColorSize();
            sizePickerRef.current?.resetSizes();
            colorPickerRef.current?.resetColors();
        }, [onChange]);
    
        useImperativeHandle(ref, () => ({
            resetVariation,
        }));
  
        return (
            <div className="w-full p-4 border rounded-lg shadow-md bg-white">
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                    Gérer les variations du produit
                </h2>
        
                {/* Choix du type de variation */}
                <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2">
                        Type de variation
                    </label>
                    <div className="flex flex-col gap-2">
                    {(["none", "size", "color", "both"] as VariationTypeEnum[]).map(
                        (type) => (
                        <label
                            key={type}
                            className={`flex items-center gap-2 cursor-pointer ${
                            variationType === type ? "text-primary" : ""
                            } ${forceIs(type) ? "text-gray-400" : ""}`}
                        >
                            <input
                                type="radio"
                                value={type}
                                className={`radio ${
                                    variationType === type ? "radio-primary" : ""
                                }`}
                                checked={variationType === type}
                                disabled={forceIs(type)}
                                onChange={() => setVariationType(type)}
                            />
                            {type === "none" && "Pas de variation (Quantité unique)"}
                            {type === "size" && "Plusieurs tailles disponibles"}
                            {type === "color" && "Plusieurs couleurs disponibles"}
                            {type === "both" &&
                            "Plusieurs couleurs et tailles disponibles"}
                        </label>
                        )
                    )}
                    </div>
                </div>
        
                {/* Quantité si pas de variation */}
                {variationType === "none" && (
                    <div className="mb-4">
                        <label className="block text-gray-700 font-semibold mb-2">
                            Quantité
                        </label>
                        <Input
                            type="number"
                            min={0}
                            className="input input-bordered w-full"
                            value={quantity}
                            onChange={(e) => {
                                const val = parseInt(e.target.value) || 0;
                                setQuantity(val);
                                onChange(variationType, { quantity: val });
                            }}
                        />
                    </div>
                )}
        
                {/* Couleurs */}
                {variationType === "color" && (
                    <ColorPicker
                        ref={colorPickerRef}
                        withQuantity
                        initialVariation={initialVariation}
                        onChange={(colors) => onChange(variationType, { colors })}
                    />
                )}
        
                {/* Tailles */}
                {variationType === "size" && (
                    <SizePicker
                        ref={sizePickerRef}
                        withQuantity
                        initialVariation={initialVariation}
                        onChange={(sizes) => onChange(variationType, { sizes })}
                    />
                )}
        
                {/* Couleurs + tailles */}
                {variationType === "both" && (
                    <ColorSizePicker
                        ref={colorSizePickerRef}
                        initialVariation={initialVariation}
                        onChange={(data) => onChange(variationType, { colorSizeData: data })}
                    />
                )}
            </div>
        );
    }
  );
